import React, { useEffect, useContext } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { dynamoProductQueries } from '../../../../graphql/queries';
import { convertLangCodeToGraphqlEnum } from '../../../../helpers/locale.js';
import Product from './Product.js';
import { LocaleContext } from '../../../../Context';
import ProductSkeleton from '../../../../Components/Skeletons/ProductSkeleton';
import NotFoundMessage from '../../../../Components/Store/NotFoundMessage.js';

// need to wrap <Product> so we can conditionally call the query
// i.e. if we try to get locale in <Product> its gonna be null (initially) when
// we try and call the query
const ProductWrapper = () => {
  const { id, language } = useParams();
  const navigate = useNavigate();

  const { locale } = useContext(LocaleContext);

  const [getProduct, { called, loading, error, data }] = useLazyQuery(dynamoProductQueries.DynamoProductById);

  useEffect(() => {
    if (locale) {
      const currency = locale.currency;
      // if the language is passed in as a QS param, use that
      // if no QS param, use the locales language
      let theLanguage = language;
      if (!theLanguage) {
        theLanguage = locale.language;
      }
      getProduct({
        variables: {
          id: parseInt(id),
          language: convertLangCodeToGraphqlEnum(theLanguage),
          currency
        }
      });
    }
  }, [getProduct, id, locale, language]);

  // Not-yet-called (locale-gated effect hasn't run) and in-flight both mean "loading".
  // A resolved-but-empty or errored result is a bad id, not a spinner — reading product
  // from query `data` (not local state) lets us tell them apart, where the old
  // `if (!product)` could only ever show the skeleton forever.
  if (!called || loading) {
    return <ProductSkeleton />;
  }

  const product = data && data.dynamoProductById;

  if (error || !product) {
    return (
      <NotFoundMessage
        message='product-not-found'
        actionLabel='browse-products'
        onAction={() => navigate('/products')}
      />
    );
  }

  return (<Product locale={locale} product={product} />);
};

export default ProductWrapper;
